import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { ICON, STATUS, TEXT } from '../../theme/tokens'
import IconAction from './IconAction'

/**
 * The most action buttons a list row or card shows. The rule (2026-10-15):
 * a list view shows at most three action buttons; when a row has more, the
 * two most important stay icons and the rest go in a menu behind a third
 * "More actions" icon. The pipeline rows had eight icons and the template
 * cards five, which crowded every row.
 */
export const MAX_VISIBLE_ACTIONS = 3

/**
 * The colours of the More actions menu's items, exported so the contrast
 * test checks exactly these (all on the menu's SURFACE.cardRaised paper).
 * A disabled item keeps a readable colour instead of MUI's 38% opacity.
 */
export const ROW_MENU = {
    item: TEXT.secondary,
    destructive: STATUS.error.fg,
    disabled: TEXT.disabled,
}

/**
 * Split a row's actions into the icons shown and the ones in the menu.
 *
 * Falsy entries are dropped first, so a caller can write
 * `canEdit && {...}`. Up to `max` actions are all icons; past it, the first
 * `max - 1` are icons and the rest are menu items, in the given order.
 *
 * @param {Array<object|false|null|undefined>} actions in order of importance
 * @param {number} [max] MAX_VISIBLE_ACTIONS by default
 * @returns {{icons: object[], menu: object[]}}
 */
export const splitActions = (actions, max = MAX_VISIBLE_ACTIONS) => {
    const present = actions.filter(Boolean)
    if (present.length <= max) return { icons: present, menu: [] }
    return { icons: present.slice(0, max - 1), menu: present.slice(max - 1) }
}

/**
 * The actions of one list row or card, never more than three buttons.
 *
 * `actions` are in order of importance; see splitActions for which become
 * icons. Each is `{key, title, icon, onClick, label?, tooltip?, note?,
 * hoverColor?, disabled?, destructive?}`: `title` is its name (the menu
 * item's text, and the icon's tooltip unless `tooltip` says more), `label`
 * the icon's accessible name, `note` a second line under a menu item (why
 * it is disabled), `destructive` draws a menu item in the error colour.
 * Choosing a menu item closes the menu, then runs `onClick(event)`.
 *
 * @param {{actions: Array<object|false|null>, name: string,
 *     stopPropagation?: boolean}} props `name` names the row in the More
 *     button's accessible name; `stopPropagation` keeps every click, menu
 *     ones included, from reaching a clickable card around the actions
 */
const RowActions = ({ actions, name, stopPropagation = false }) => {
    const [anchor, setAnchor] = useState(null)
    const { icons, menu } = splitActions(actions)
    const close = () => setAnchor(null)

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {icons.map((a) => (
                <IconAction
                    key={a.key}
                    title={a.tooltip ?? a.title}
                    label={a.label ?? a.title}
                    onClick={a.onClick}
                    hoverColor={
                        a.hoverColor ??
                        (a.destructive ? STATUS.error.fg : TEXT.primary)
                    }
                    disabled={a.disabled}
                    stopPropagation={stopPropagation}
                >
                    {a.icon}
                </IconAction>
            ))}
            {menu.length > 0 && (
                <>
                    <IconAction
                        title="More actions"
                        label={`More actions for ${name}`}
                        popup="menu"
                        onClick={(e) => setAnchor(e.currentTarget)}
                        stopPropagation={stopPropagation}
                    >
                        <MoreVertIcon />
                    </IconAction>
                    <Menu
                        anchorEl={anchor}
                        open={Boolean(anchor)}
                        onClose={close}
                        onClick={
                            stopPropagation
                                ? (e) => e.stopPropagation()
                                : undefined
                        }
                    >
                        {menu.map((a) => {
                            const color = a.destructive
                                ? ROW_MENU.destructive
                                : ROW_MENU.item
                            return (
                                <MenuItem
                                    key={a.key}
                                    disabled={a.disabled}
                                    onClick={(e) => {
                                        // MUI blocks a disabled item only
                                        // with pointer-events in CSS; its
                                        // click handler still runs.
                                        if (a.disabled) return
                                        close()
                                        a.onClick(e)
                                    }}
                                    sx={{
                                        color,
                                        '& .MuiListItemIcon-root': {
                                            color,
                                            minWidth: 30,
                                        },
                                        '& svg': { fontSize: ICON.lg },
                                        '&.Mui-disabled': {
                                            opacity: 1,
                                            color: ROW_MENU.disabled,
                                            '& .MuiListItemIcon-root, & .MuiListItemText-secondary':
                                                { color: ROW_MENU.disabled },
                                        },
                                    }}
                                >
                                    <ListItemIcon>{a.icon}</ListItemIcon>
                                    <ListItemText
                                        primary={a.title}
                                        secondary={a.note}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                        }}
                                    />
                                </MenuItem>
                            )
                        })}
                    </Menu>
                </>
            )}
        </Box>
    )
}

RowActions.propTypes = {
    actions: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.shape({
                key: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                icon: PropTypes.node.isRequired,
                onClick: PropTypes.func.isRequired,
                label: PropTypes.string,
                tooltip: PropTypes.string,
                note: PropTypes.string,
                hoverColor: PropTypes.string,
                disabled: PropTypes.bool,
                destructive: PropTypes.bool,
            }),
            PropTypes.oneOf([false, null, undefined]),
        ])
    ).isRequired,
    name: PropTypes.string.isRequired,
    stopPropagation: PropTypes.bool,
}

export default RowActions
